import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * Creates a lazy-loaded component with retry logic for failed dynamic imports
 * This handles network errors, CDN issues, and other transient failures
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  delay = 1000
): LazyExoticComponent<T> {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const attemptImport = (attempt: number) => {
        importFn()
          .then(resolve)
          .catch((error) => {
            if (attempt < retries) {
              console.warn(
                `Failed to load module (attempt ${attempt + 1}/${retries}), retrying...`,
                error
              );
              setTimeout(() => attemptImport(attempt + 1), delay * attempt);
            } else {
              console.error('Failed to load module after all retries:', error);
              // Show user-friendly error
              reject(
                new Error(
                  'Failed to load page. Please check your internet connection and try refreshing the page.'
                )
              );
            }
          });
      };
      attemptImport(0);
    });
  });
}
